import { Filter, MetaData, SearchFilter, SearchOptions } from './search-types';
import useUtils from '../../../utils/use-utils';
import useHttpService, { httpInstance } from '../../services/http-service';
import { snakeCase } from 'lodash';
import useSearchUtils from './use-search-utils';
import { ListSearchType } from './list-search-types';
import { AxiosResponse } from 'axios';
import { ExportOptions } from '../../../export/export-types';
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export default function useSearch() {
  const { transformFilter } = useSearchUtils();
  const { httpGet } = useHttpService();
  const { getProperty } = useUtils();
  const convertSearchFilterToQuery = (filter: SearchFilter): Filter => {
    const basicSearchKeys: Array<keyof SearchFilter> = [];
    const queryObject: Filter = {};

    for (const key in filter) {
      basicSearchKeys.push(key as keyof SearchFilter);
    }

    basicSearchKeys.forEach((baseKey) => {
      const selectedFilter = getProperty<SearchFilter, Filter>(filter, baseKey);
      for (const filterKey in selectedFilter) {
        const filterValue = selectedFilter[filterKey];
        if (filterValue === '') {
          continue;
        }

        if (Array.isArray(filterValue) && filterValue.length === 0) {
          continue;
        }

        const snakeCasedFilterKey = transformFilter(filterKey, snakeCase);
        const resolvedFilter = `${baseKey}[${snakeCasedFilterKey}]`;
        queryObject[resolvedFilter] = Array.isArray(filterValue)
          ? filterValue.join(',')
          : filterValue;
      }
    });

    return queryObject;
  };

  const search = async <ResponseType>(
    route: string,
    filter: SearchFilter,
    additionalParams: Filter | null = null,
    config?: any
  ): Promise<ResponseType> => {
    const params = {
      ...additionalParams,
      ...convertSearchFilterToQuery(filter),
    };

    return httpGet<ResponseType>(route, {
      params,
      ...config,
    });
  };

  const listSearch = <T>(searchParams: SearchOptions): Promise<T> => {
    return search<T>('/list-search', searchParams.searchFilter, {
      ...searchParams.searchOption,
    });
  };

  const listExport = (
    exportOptions: ExportOptions,
    index: string
  ): Promise<AxiosResponse> => {
    const params = {
      type: exportOptions.type,
      ...convertSearchFilterToQuery(exportOptions.searchFilter),
      index,
    };

    return httpInstance.get('/export', {
      params,
    });
  };

  const getListSearchMeta = <T = MetaData>(
    type: ListSearchType,
    metaInfo?: { [key: string]: string }
  ): Promise<T> => {
    const dataToSend: { [key: string]: unknown } = {
      type,
    };

    if (metaInfo) {
      dataToSend['metaInfo'] = metaInfo;
    }

    return httpGet('/meta-data', { params: { ...dataToSend } });
  };
  return {
    search,
    getListSearchMeta,
    listSearch,
    listExport,
  };
}
